import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  Search,
  Clock,
  User,
  MoreHorizontal,
  Bot,
  BarChart3,
  FileText,
  RefreshCw,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/hooks/useToast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

interface BlogPost {
  id: string;
  titulo: string;
  slug: string;
  resumo: string;
  status: 'PUBLICADO' | 'RASCUNHO' | 'AGENDADO';
  visualizacoes: number;
  publicadoEm: string | null;
  createdAt: string;
  tempoLeitura: number;
  geradoPorIA: boolean;
  categoria: {
    nome: string;
  };
  autor: {
    nome: string;
  };
}

interface Estatisticas {
  totalPosts: number;
  postsPublicados: number;
  postsRascunho: number;
  totalVisualizacoes: number;
  postsGeradosIA: number;
}

export default function BlogManagement() {
  const { toast } = useToast();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [postParaDeletar, setPostParaDeletar] = useState<BlogPost | null>(null);
  const [gerandoManualmente, setGerandoManualmente] = useState(false);

  useEffect(() => {
    carregarDados();
  }, [statusFilter]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [postsRes, statsRes] = await Promise.all([
        api.get('/blog/admin/posts', {
          params: { status: statusFilter || undefined },
        }),
        api.get('/blog/estatisticas'),
      ]);
      setPosts(postsRes.data.posts);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados do blog.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async () => {
    if (!postParaDeletar) return;

    try {
      await api.delete(`/blog/posts/${postParaDeletar.id}`);
      setPosts(posts.filter((p) => p.id !== postParaDeletar.id));
      toast({
        title: 'Sucesso',
        description: 'Post deletado com sucesso.',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível deletar o post.',
        variant: 'destructive',
      });
    } finally {
      setPostParaDeletar(null);
    }
  };

  const gerarArtigoManual = async () => {
    try {
      setGerandoManualmente(true);
      // Chamar endpoint de geração manual
      await api.post('/blog/gerar-manual');
      toast({
        title: 'Sucesso',
        description: 'Artigo gerado com sucesso!',
      });
      carregarDados();
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível gerar o artigo.',
        variant: 'destructive',
      });
    } finally {
      setGerandoManualmente(false);
    }
  };

  const formatarData = (data: string | null) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLICADO':
        return 'bg-green-100 text-green-700';
      case 'RASCUNHO':
        return 'bg-yellow-100 text-yellow-700';
      case 'AGENDADO':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const postsFiltrados = posts.filter(
    (post) =>
      post.titulo.toLowerCase().includes(busca.toLowerCase()) ||
      post.resumo.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#1a1a2e]">Gerenciar Blog</h1>
              <p className="text-gray-500 mt-1">
                Crie, edite e gerencie os artigos do blog
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={gerarArtigoManual}
                disabled={gerandoManualmente}
              >
                <Bot
                  className={`w-4 h-4 mr-2 ${
                    gerandoManualmente ? 'animate-spin' : ''
                  }`}
                />
                {gerandoManualmente ? 'Gerando...' : 'Gerar com IA'}
              </Button>
              <Link to="/admin/blog/novo">
                <Button variant="nuvem">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Artigo
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        {estatisticas && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {[
              {
                label: 'Total de Posts',
                value: estatisticas.totalPosts,
                icon: FileText,
                color: 'bg-blue-500',
              },
              {
                label: 'Publicados',
                value: estatisticas.postsPublicados,
                icon: Eye,
                color: 'bg-green-500',
              },
              {
                label: 'Rascunhos',
                value: estatisticas.postsRascunho,
                icon: Edit2,
                color: 'bg-yellow-500',
              },
              {
                label: 'Visualizações',
                value: estatisticas.totalVisualizacoes.toLocaleString(),
                icon: BarChart3,
                color: 'bg-purple-500',
              },
              {
                label: 'Gerados por IA',
                value: estatisticas.postsGeradosIA,
                icon: Bot,
                color: 'bg-pink-500',
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-4 rounded-xl shadow-sm"
              >
                <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-2xl font-bold text-[#1a1a2e]">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar artigos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="PUBLICADO">Publicado</option>
              <option value="RASCUNHO">Rascunho</option>
              <option value="AGENDADO">Agendado</option>
            </select>
            <Button variant="outline" onClick={carregarDados}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Lista de posts */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full" />
          </div>
        ) : postsFiltrados.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Nenhum artigo encontrado
            </h3>
            <p className="text-gray-500 mb-6">
              Comece criando seu primeiro artigo ou gerando um com IA.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/admin/blog/novo">
                <Button variant="nuvem">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Artigo
                </Button>
              </Link>
              <Button variant="outline" onClick={gerarArtigoManual}>
                <Bot className="w-4 h-4 mr-2" />
                Gerar com IA
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-gray-700">
                    Artigo
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Categoria
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Visualizações
                  </th>
                  <th className="text-left py-4 px-4 font-medium text-gray-700">
                    Data
                  </th>
                  <th className="text-right py-4 px-6 font-medium text-gray-700">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {postsFiltrados.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#1a1a2e] truncate">
                            {post.titulo}
                          </h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {post.autor.nome}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.tempoLeitura} min
                            </span>
                            {post.geradoPorIA && (
                              <span className="flex items-center gap-1 text-purple-600">
                                <Bot className="w-3 h-3" />
                                IA
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-600">
                        {post.categoria.nome}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          post.status
                        )}`}
                      >
                        {post.status}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-600">
                        {post.visualizacoes.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-gray-500">
                        {formatarData(post.publicadoEm || post.createdAt)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              Visualizar
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Link
                              to={`/admin/blog/${post.id}/editar`}
                              className="flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Editar
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => setPostParaDeletar(post)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deletar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Dialog de confirmação */}
        <Dialog open={!!postParaDeletar} onOpenChange={() => setPostParaDeletar(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar exclusão</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja deletar o artigo "{postParaDeletar?.titulo}"?
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPostParaDeletar(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeletar}>
                <Trash2 className="w-4 h-4 mr-2" />
                Deletar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
